import { startOfMonth, endOfMonth, startOfYear, endOfYear, startOfWeek, endOfWeek, startOfDay, endOfDay, subDays, subWeeks, subMonths, subYears } from 'date-fns';
import { dateToLocalString, localStringToDate, isDateInRange } from '@/utils/dateFormatUtils';

export const shouldCreateHistoryEntry = (budget: any, currentDate: Date = new Date()): boolean => {
  if (!budget.period) return false;

  const { endDate } = calculateBudgetPeriodDates(budget.period, currentDate);
  
  // Verifica se a data atual está fora do período atual (período terminou)
  const todayStr = dateToLocalString(currentDate);
  return todayStr > endDate;
};

export const getLastPeriodDates = (period: string, referenceDate: Date = new Date()): { startDate: string; endDate: string } => {
  let startDate: Date;
  let endDate: Date;

  switch (period) {
    case 'diario': {
      const yesterday = subDays(referenceDate, 1);
      const dateString = dateToLocalString(yesterday);
      return {
        startDate: dateString,
        endDate: dateString,
      };
    }
    case 'semanal': {
      const lastWeekEnd = subDays(startOfWeek(referenceDate, { weekStartsOn: 1 }), 1);
      startDate = startOfWeek(lastWeekEnd, { weekStartsOn: 1 });
      endDate = endOfWeek(lastWeekEnd, { weekStartsOn: 1 });
      break;
    }
    case 'mensal': {
      const lastMonth = subMonths(referenceDate, 1);
      startDate = startOfMonth(lastMonth);
      endDate = endOfMonth(lastMonth);
      break;
    }
    case 'anual': {
      const lastYear = subYears(referenceDate, 1);
      startDate = startOfYear(lastYear);
      endDate = endOfYear(lastYear);
      break;
    }
    default: {
      const defaultDate = new Date();
      const d = dateToLocalString(defaultDate);
      return {
        startDate: d,
        endDate: d,
      };
    }
  }

  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate),
  };
};

export const calculateBudgetPeriodDates = (period: string, referenceDate: Date = new Date()): { startDate: string; endDate: string } => {
  let startDate: Date;
  let endDate: Date;

  switch (period) {
    case 'diario':
      startDate = startOfDay(referenceDate);
      endDate = endOfDay(referenceDate);
      break;
    case 'semanal':
      startDate = startOfWeek(referenceDate, { weekStartsOn: 1 });
      endDate = endOfWeek(referenceDate, { weekStartsOn: 1 });
      break;
    case 'mensal':
      startDate = startOfMonth(referenceDate);
      endDate = endOfMonth(referenceDate);
      break;
    case 'anual':
      startDate = startOfYear(referenceDate);
      endDate = endOfYear(referenceDate);
      break;
    default: {
      const d = dateToLocalString(new Date());
      return { startDate: d, endDate: d };
    }
  }

  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate),
  };
};

export const calculatePeriodSpent = (
  transactions: any[],
  startDate: string,
  endDate: string,
  categoryId?: string
): number => {
  const start = localStringToDate(startDate);
  const end = localStringToDate(endDate);

  const relevantTransactions = transactions.filter(transaction => {
    const isInPeriod = isDateInRange(transaction.date, start, end);
    const isExpense = transaction.type === 'despesa';
    const isEffective = transaction.status === 'paid' || transaction.status === 'efetivada' || transaction.status === 'partial';
    const matchesCategory = !categoryId || transaction.category_id === categoryId || transaction.categoryId === categoryId;
    const isNotDeleted = !transaction.deleted;

    const included = isInPeriod && isExpense && isEffective && matchesCategory && isNotDeleted;
    
    // Validação: avisar se amount está zerado (possível problema de descriptografia)
    if (included && transaction.amount === 0) {
      console.warn(`[calculatePeriodSpent] ⚠️ Transaction ${transaction.id} has amount = 0. Check if decryption completed.`, {
        date: transaction.date,
        description: transaction.description,
        amount_encrypted: transaction.amount_encrypted ? 'exists' : 'missing'
      });
    }

    // Log para debug de transações no período
    if (included) {
      console.log(`[calculatePeriodSpent] ✓ Included Transaction:`, {
        id: transaction.id,
        date: transaction.date,
        amount: transaction.amount,
        description: transaction.description,
        status: transaction.status,
        categoryId: transaction.categoryId || transaction.category_id
      });
    }

    return included;
  });

  const total = relevantTransactions.reduce((sum, transaction) => {
    return sum + Math.abs(transaction.amount);
  }, 0);

  console.log(`[calculatePeriodSpent] Period ${startDate} to ${endDate}: ${relevantTransactions.length} transactions, total: R$ ${total.toFixed(2)}`);
  
  return total;
};
